import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, Trash2 } from "lucide-react";
import { formatBytes } from "@/lib/formatBytes";
import { fetchMailStorageStats, type MailStorageStats } from "@/lib/api/storage";
import { MAIL_STORAGE_INBOX_MAX } from "@/lib/constants/mailStorage";
import {
  purgeServerMailStorage,
  resetAccountDd02Storage,
} from "@/lib/storage/mailStoragePurge";
import { retryInboxServerArchive } from "@/lib/storage/inboxLocalStorageSync";
import { reloadStandardFoldersInBackground } from "@/lib/mail/accountMailCache";
import { notifyMailStorageStatsRefresh } from "@/features/accounts/MailStorageQuotaSection";
import { MailConfirmDialog } from "@/features/shared/MailConfirmDialog";
import { showMailToast } from "@/lib/ui/mailToast";
import { cn } from "@/lib/cn";
import type { MailFolder } from "@/lib/types/mail";

const WARN_PERCENT = 80;

type Props = {
  accountId: number;
  folders?: MailFolder[];
  active?: boolean;
  compact?: boolean;
  onPurged?: () => void;
};

function defaultPurgeDateInput(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return d.toISOString().slice(0, 10);
}

export function MailLocalStorageSection({
  accountId,
  folders = [],
  active = true,
  compact = false,
  onPurged,
}: Props) {
  const [stats, setStats] = useState<MailStorageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purgeDate, setPurgeDate] = useState(defaultPurgeDateInput);
  const [purgeFolder, setPurgeFolder] = useState("");

  const folderOptions = useMemo(() => {
    const names = new Set<string>();
    for (const row of stats?.folders ?? []) names.add(row.folder);
    for (const f of folders) names.add(f.name);
    return [...names].sort((a, b) => a.localeCompare(b, "fr"));
  }, [folders, stats?.folders]);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMailStorageStats(accountId);
      if (!res.ok || !res.data.success) {
        throw new Error(!res.ok ? res.message : "Lecture stockage impossible.");
      }
      setStats(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de lire le stockage serveur.");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (!active) return;
    void loadStats();
    const onRefresh = () => void loadStats();
    window.addEventListener("mail-storage-stats-refresh", onRefresh);
    return () => window.removeEventListener("mail-storage-stats-refresh", onRefresh);
  }, [accountId, active, loadStats]);

  async function handleClearAccountStorage() {
    if (purging) return;
    setPurging(true);
    setError(null);
    setConfirmClearOpen(false);
    try {
      await resetAccountDd02Storage(accountId);
      reloadStandardFoldersInBackground(accountId, folders ?? []);
      void retryInboxServerArchive(accountId);
      await loadStats();
      notifyMailStorageStatsRefresh();
      onPurged?.();
      showMailToast(
        "Stockage réinitialisé. Liste rechargée, copie de la réception relancée.",
        "success"
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Purge impossible.");
    } finally {
      setPurging(false);
    }
  }

  async function handleRetryInboxArchive() {
    if (purging) return;
    setPurging(true);
    setError(null);
    try {
      await retryInboxServerArchive(accountId);
      showMailToast("Copie de la réception relancée.", "info");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Relance impossible.");
    } finally {
      setPurging(false);
    }
  }

  async function handlePurgeBeforeDate() {
    if (purging || !purgeDate) return;
    setPurging(true);
    setError(null);
    try {
      const result = await purgeServerMailStorage({
        accountId,
        beforeDate: purgeDate,
        folder: purgeFolder || undefined,
      });
      await loadStats();
      notifyMailStorageStatsRefresh();
      onPurged?.();
      showMailToast(
        result.removed > 0
          ? `${result.removed} e-mail${result.removed > 1 ? "s" : ""} retiré${result.removed > 1 ? "s" : ""} (${formatBytes(result.freedBytes)}).`
          : "Aucun e-mail copié pour cette période.",
        result.removed > 0 ? "success" : "info"
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Purge impossible.");
    } finally {
      setPurging(false);
    }
  }

  async function handlePurgeFolder() {
    if (purging || !purgeFolder) return;
    setPurging(true);
    setError(null);
    try {
      const result = await purgeServerMailStorage({ accountId, folder: purgeFolder });
      await loadStats();
      notifyMailStorageStatsRefresh();
      onPurged?.();
      showMailToast(
        result.removed > 0
          ? `Dossier vidé sur le serveur — ${result.removed} e-mail${result.removed > 1 ? "s" : ""}.`
          : "Aucun e-mail copié dans ce dossier.",
        result.removed > 0 ? "success" : "info"
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Purge impossible.");
    } finally {
      setPurging(false);
    }
  }

  const inboxCount = stats?.inboxCount ?? 0;
  const usedBytes = stats?.usedBytes ?? 0;
  const maxBytes = stats?.maxBytes ?? 0;
  const usedPercent = maxBytes > 0 ? Math.min(100, (usedBytes / maxBytes) * 100) : 0;
  const barPercent = Math.min(100, Math.max(0, usedPercent));
  const isWarn = usedPercent >= WARN_PERCENT;
  const emlCount = stats?.emlMessageCount ?? 0;

  return (
    <div className={cn("space-y-4", compact && "text-xs")}>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-[var(--mail-muted)]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement…
        </div>
      ) : error ? (
        <div className="space-y-2">
          <p className="text-xs text-amber-700">{error}</p>
          <button
            type="button"
            onClick={() => void loadStats()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--mail-border-soft)] px-3 py-1.5 text-xs font-medium text-[var(--mail-text)] transition hover:bg-[var(--mail-surface-hover)]"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            Réessayer
          </button>
        </div>
      ) : stats ? (
        <>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className={isWarn ? "font-semibold text-amber-700" : "text-[var(--mail-text)]"}>
                {formatBytes(usedBytes)} / {formatBytes(maxBytes)}
              </span>
              <span className="text-[var(--mail-muted)]">{Math.round(usedPercent)} %</span>
            </div>
            <div
              className="h-2 overflow-hidden rounded-full bg-[var(--mail-surface-hover)]"
              role="progressbar"
              aria-valuenow={barPercent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  isWarn ? "bg-amber-500" : "bg-[var(--mail-accent)]"
                )}
                style={{ width: `${barPercent}%` }}
              />
            </div>
            <p className="text-[11px] leading-relaxed text-[var(--mail-muted)]">
              Réception : {inboxCount} / {MAIL_STORAGE_INBOX_MAX} e-mails copiés sur le serveur.
              Les pastilles vertes indiquent un message déjà chargé dans l’app (ouverture sans attente).
            </p>
          </div>

          {(stats.folders?.length ?? 0) > 0 ? (
            <div className="space-y-1.5 rounded-lg border border-[var(--mail-border-soft)] px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--mail-muted)]">
                Par dossier
              </p>
              <ul className="space-y-1 text-[11px] text-[var(--mail-text)]">
                {stats.folders?.map((row) => (
                  <li key={row.folder} className="flex justify-between gap-2">
                    <span className="truncate">{row.folder}</span>
                    <span className="shrink-0 tabular-nums text-[var(--mail-muted)]">
                      {row.messageCount} · {formatBytes(row.usedBytes)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="space-y-3 border-t border-[var(--mail-border-soft)] pt-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--mail-muted)]">
              Libérer de l&apos;espace
            </p>
            <p className="text-[11px] leading-relaxed text-[var(--mail-muted)]">
              Retire les copies sur le serveur Aurélia — rien n&apos;est supprimé chez votre fournisseur mail.
            </p>

            <label className="block space-y-1">
              <span className="text-xs font-medium text-[var(--mail-text)]">Antérieur au</span>
              <input
                type="date"
                value={purgeDate}
                disabled={purging}
                onChange={(e) => setPurgeDate(e.target.value)}
                className="w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-xs"
              />
            </label>

            <label className="block space-y-1">
              <span className="text-xs font-medium text-[var(--mail-text)]">Dossier (optionnel)</span>
              <select
                value={purgeFolder}
                disabled={purging}
                onChange={(e) => setPurgeFolder(e.target.value)}
                className="w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-xs"
              >
                <option value="">Tous les dossiers</option>
                {folderOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void handlePurgeBeforeDate()}
                disabled={purging || emlCount === 0}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--mail-border-soft)] px-3 py-2 text-xs font-medium text-[var(--mail-text)] transition hover:bg-[var(--mail-surface-hover)] disabled:opacity-50"
              >
                {purging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Retirer avant cette date
              </button>
              <button
                type="button"
                onClick={() => void handlePurgeFolder()}
                disabled={purging || !purgeFolder}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--mail-border-soft)] px-3 py-2 text-xs font-medium text-[var(--mail-text)] transition hover:bg-[var(--mail-surface-hover)] disabled:opacity-50"
              >
                Vider ce dossier
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 border-t border-[var(--mail-border-soft)] pt-3">
            <button
              type="button"
              onClick={() => void handleRetryInboxArchive()}
              disabled={purging}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--mail-border-soft)] px-3 py-2 text-xs font-medium text-[var(--mail-text)] transition hover:bg-[var(--mail-surface-hover)] disabled:opacity-50"
            >
              {purging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Relancer la copie de la réception
            </button>
          </div>

          <button
            type="button"
            onClick={() => setConfirmClearOpen(true)}
            disabled={purging || (stats.usedBytes <= 0 && emlCount === 0)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-700 transition hover:bg-red-50 disabled:opacity-50"
          >
            {purging ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Tout réinitialiser
          </button>
        </>
      ) : null}

      <MailConfirmDialog
        open={confirmClearOpen}
        title="Réinitialiser le stockage serveur ?"
        message="Les copies, en-têtes en cache et données de session de ce compte seront supprimés sur le serveur Aurélia. Vos e-mails restent chez votre fournisseur. Les pastilles vertes réapparaîtront après la prochaine copie."
        confirmLabel="Réinitialiser"
        busy={purging}
        onConfirm={() => void handleClearAccountStorage()}
        onCancel={() => setConfirmClearOpen(false)}
      />
    </div>
  );
}
